/* eslint-disable max-len */
import { h } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import Storefront from 'utils/storefront';
import Image from "utils/image";
import CartItemPrice from './cart-item-price';
import { withCartContext } from './cart-context';
import { productGet } from '../utils/storefront/queries';


const CartItem = ({ cart, line, isB2B }) => {
  const [errors, setErrors] = useState({maxQuantity: false, minQuantity: false});
  const [shippingStatusText, setShippingStatusText] = useState('');
  const [shippingStatusInStock, setShippingStatusInStock] = useState('');
  const [lineVariant, setLineVariant] = useState('');
  const [variantAvailable, setVariantAvailable] = useState(true);
  const storefront = Storefront.getInstance();
	
	const quantityIncrement = line?.properties?._quantityRule?.increment || 1;
	const minQuantity = line?.properties?._quantityRule?.min || 1;
	const maxQuantity = isB2B ? line?.properties?._quantityRule?.max : line?.properties?._inventoryQuantity;

  const findVariantById = (variants, id) => {
    const gid = `gid://shopify/ProductVariant/${id}`
    return variants?.find(variant => variant.node.id === gid)?.node || null;
  };

  const getLineProductData = async () => {
    const lineProductGid = `gid://shopify/Product/${line.product_id}`
    const response = await storefront.request({
      query: productGet,
      variables: {
        id: lineProductGid
      },
    });

    if (!response.data) {
      // eslint-disable-next-line no-console
      console.error(
        'cart-item.js: Storefront API request failed',
        response,
      );
      return [];
    }

    return response.data;
  };

  const processLineProductData = (lineProductData) => {

    const variantData = findVariantById(
      lineProductData.product?.variants?.edges,
      line.id
    );

		if (lineProductData?.product?.retailPrice?.value && !variantData?.retailPrice?.value) {
			variantData.retailPrice = lineProductData.product.retailPrice
		}

    if (lineProductData?.product?.sellInSets?.value){
      variantData.sellInSets = lineProductData.product.sellInSets
    }

    const shippingStatusValue =
			line.quantity > variantData?.quantityAvailable
				? variantData?.negativeInventoryShippingStatus?.value
				: variantData?.shippingStatus?.value ||
					lineProductData?.product?.shippingStatus?.value;

    const shippingStatusInStockValue = shippingStatusValue
      ? shippingStatusValue.toLowerCase().includes('in stock')
      : false;

    setShippingStatusText(shippingStatusValue);
    setShippingStatusInStock(shippingStatusInStockValue);
		setLineVariant(variantData);
    if (variantData?.continueSellingOnlyForB2b?.value === 'true' && !isB2B) {
      setVariantAvailable(variantData?.quantityAvailable > 0 && line.quantity <= variantData?.quantityAvailable)
    } else if(!isB2B && (variantData?.continueSellingOnlyForB2b?.value !== 'false' || !variantData?.continueSellingOnlyForB2b?.value)) {
      setVariantAvailable(variantData?.quantityAvailable > 0 && line.quantity <= variantData?.quantityAvailable)
    }
    else {
      setVariantAvailable(variantData?.availableForSale);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (line && line.variant_id) {
        try {
          const lineProductData = await getLineProductData();
          processLineProductData(lineProductData);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('cart-item.js: Storefront API request failed', error);
        }
      }
    };

    fetchData();
  }, [line.variant_id, line.quantity]);
 
  const handleAdjustQuantity = (e) => {
		const qty = Number.parseInt(e.target.value, 10);
		let newQuantity

    if (qty > maxQuantity) {
			newQuantity = maxQuantity
    } else if (qty < minQuantity) {
			newQuantity = minQuantity
    } else if (quantityIncrement && (qty % quantityIncrement)) {
			newQuantity = Math.max(Math.min(Math.floor(newQuantity / quantityIncrement) * quantityIncrement, minQuantity), minQuantity)
		} 
    
		setErrors({ ...errors, maxQuantity: qty > maxQuantity, minQuantity: qty < minQuantity });

    return cart.update([
      {
        id: line.id,
        quantity: newQuantity,
      },
    ]);
  };

  const handleAdjustQuantityMinus = (quantity) => {
		const qtyIncrement = line?.properties?._quantityRule?.increment || 1;
		const qtyMax = line?.properties?._quantityRule?.max || maxQuantity;
		const qtyMin = line?.properties?._quantityRule?.min || 1;
		const newQuantity = quantity - qtyIncrement >= qtyMin ? quantity - qtyIncrement : 0;
		if (newQuantity < qtyMin && line.properties?._quantityPriceBreaksConfigured) {
			setErrors({ ...errors, minQuantity: true });
		}
		if (newQuantity <= qtyMax) {
			setErrors({ ...errors, maxQuantity: false });
		}
		return cart.update([
			{
				id: line.id,
				quantity: newQuantity,
			},
		]);
  }

	const handleAdjustQuantityPlus = (quantity) => {
		const qtyIncrement = line?.properties?._quantityRule?.increment || 1;
		const qtyMax = line?.properties?._quantityRule?.max || maxQuantity;
		const newQuantity = Math.min(quantity + (qtyIncrement || 1));
		if (line.properties?._quantityPriceBreaksConfigured) {
			setErrors({ ...errors, minQuantity: false });
		}
		if (newQuantity > qtyMax) {
			setErrors({ ...errors, maxQuantity: true });
		}
		// eslint-disable-next-line consistent-return
		return cart.update([
			{
				id: line.id,
				quantity: newQuantity,
			},
		]);
  }

  const handleRemove = () => {
    let payload = [line.id];

    // If bundle, add children to update request
    if (line.children) {
      payload = line.children.map((child) => child.id);
    }

    return cart.remove(payload);
  };

	const decrementDisabled = line.quantity <= line?.properties?._quantityRule?.min || line.quantity === 1
	const incrementDisabled = (maxQuantity && line.quantity >= maxQuantity) || maxQuantity - line.quantity < line.properties?._quantityRule?.increment
	const inputDisabled = line.properties?._quantityPriceBreaksConfigured || line.properties?._quantityRule?.min > 1 || line.properties?._quantityRule?.max || line.properties?._quantityRule?.increment > 1

  return (
		<div
			className={`
				relative flex flex-col gap-1 md:gap-2 mx-3 md:mx-5
				${line.product_type.toLowerCase() === 'swatch' && 'my-3 md:mt-5'}`}
			key={line.id}
		>
			<div className="flex gap-2 basis-0">
				<a
					className={` 
						${line.product_type.toLowerCase() === 'swatch' ? 'w-28' : 'w-full lg:w-68'}`}
					href={`/products/${line.handle}`}
				>
					<Image
						classes="h-auto"
						src={line.image}
						srcWidths={{ sm: 200, md: 400, lg: 680 }}
						width={line.featured_image.width}
						height={line.featured_image.height}
						alt={line.featured_image.altText}
					/>
				</a>
				<div className="flex flex-col justify-between w-full">
					<div className="flex flex-col gap-1">
						<a
							className="no-underline text-primary utility-2-bold"
							href={`/products/${line.handle}`}
						>
							{line.product_title}
						</a>
						{line.product_type.toLowerCase() !== 'swatch' ? (
							<CartItemPrice line={line} variant={lineVariant} isB2B={isB2B} />
						) : (
							<p className="utility-2 text-primary uppercase">
								{window.TMW.Strings.cart.swatchFree}
							</p>
						)}
						{line.discounts?.map((discount, index) => (
							<p className="body-2 text-red" key={discount.title + index}>
								{discount.title}
							</p>
						))}
						{line.sku && line.product_type.toLowerCase() !== 'swatch' && (
							<p className="body-2 text-secondary">{line.sku}</p>
						)}
						<div className="gap-0.5">
							{line.options_with_values?.map((option, index) => (
								<p
									className={`${
										option.value === 'Default Title'
											? 'hidden'
											: 'body-2 text-secondary'
									}`}
									key={option.name + index}
								>
									{option.name}: {option.value}
								</p>
							))}
						</div>
						{shippingStatusText && (
							<div
								className={`md:inline body-2 
										${!shippingStatusInStock && 'text-error font-bold'}
										${line.product_type.toLowerCase() !== 'swatch' && 'hidden'}
										`}
							>
								{shippingStatusText}
							</div>
						)}
						{errors?.maxQuantity && variantAvailable && (lineVariant?.continueSellingOnlyForB2b?.value !== 'false' || !lineVariant?.continueSellingOnlyForB2b?.value) && (
							<p className="hidden md:inline text-error body-2 font-bold">
								{window.TMW.Strings.cart.errors.maxQuantity}
							</p>
						)}
						{errors?.minQuantity && (
							<p className="hidden md:inline text-error body-2 font-bold">
								{window.TMW.Strings.cart.errors.minQuantity}
							</p>
						)}
						{!variantAvailable &&
							line.product_type.toLowerCase() !== 'swatch' && (
								<div className="hidden md:flex w-full p-2 mb-1.5 bg-red justify-center items-center">
									<p className="text-white font-bold body-2">
										{lineVariant?.quantityAvailable
											? `Only ${lineVariant.quantityAvailable} left in stock, please update the quantity.`
											: `Product is out of stock, please remove from your cart.`}
									</p>
								</div>
							)}
					</div>
					<div
						className={`
								hidden md:flex 
								${
									line.product_type.toLowerCase() !== 'swatch'
										? 'justify-between'
										: 'justify-end'
								}
							`}
					>
						{line.product_type.toLowerCase() !== 'swatch' && (
							<div className="border border-grey-5 px-2 py-1 flex items-center gap-2 utility-2 text-secondary">
								<button
									className={`w-5 ${decrementDisabled && 'opacity-50'}`}
									onClick={() => handleAdjustQuantityMinus(line.quantity)}
									aria-label={window.TMW.Strings.cart.remove}
									disabled={decrementDisabled}
								>&#8722;</button>
								<input
									type="number"
									value={line.quantity}
									className="p-0 border-0 w-5 text-center bg-white"
									onBlur={handleAdjustQuantity}
									disabled={inputDisabled}
									max={maxQuantity}
								/>
								<button
									className={`w-5 ${incrementDisabled && 'opacity-50'}`}
									onClick={() => handleAdjustQuantityPlus(line.quantity)}
									aria-label={window.TMW.Strings.cart.add}
								>
									&#43;
								</button>
							</div>
						)}
						<button
							type="button"
							className="body-2 md:body-1 underline"
							onClick={() => handleRemove()}
						>
							{window.TMW.Strings.cart.remove}
						</button>
					</div>
				</div>
			</div>
			{!variantAvailable && line.product_type.toLowerCase() !== 'swatch' && (
				<div className="md:hidden flex  w-full p-2 bg-red justify-center items-center">
					<p className="text-white font-bold body-2">
						{lineVariant?.quantityAvailable
							? `Only ${lineVariant.quantityAvailable} left in stock, please update the quantity.`
							: `Product is out of stock, please remove from your cart.`}
					</p>
				</div>
			)}
			{shippingStatusText && line.product_type.toLowerCase() !== 'swatch' && (
				<div
					className={`md:hidden w-full text-center py-2 body-2 bg-grey-5 ${
						!shippingStatusInStock && 'text-error font-bold'
					}`}
				>
					{shippingStatusText}
				</div>
			)}
			<div
				className={`md:hidden ${
					line.product_type.toLowerCase() === 'swatch'
						? 'absolute bottom-0 right-0'
						: 'flex justify-between'
				}`}
			>
				<div
					className={`
						${line.product_type.toLowerCase() === 'swatch' ? 'w-full' : 'w-1/2'}
					`}
				>
					{errors?.maxQuantity && (
						<p className="md:hidden text-error body-2 font-bold mb-2">
							{window.TMW.Strings.cart.errors.maxQuantity}
						</p>
					)}
					{line.product_type.toLowerCase() !== 'swatch' && (
						<div className="border border-secondary px-4 py-1 flex items-center gap-2 utility-2 text-secondary">
							<button
								className={`w-10 ${decrementDisabled && 'opacity-50'}`}
								onClick={() => handleAdjustQuantityMinus(line.quantity)}
								aria-label={window.TMW.Strings.cart.remove}
								disabled={decrementDisabled}
							>
								&#8722;
							</button>
							<input
								type="number"
								value={line.quantity}
								className="p-0 border-0 w-full text-center"
								onBlur={(e) => handleAdjustQuantity(Number.parseInt(e.target.value, 10))}
								max={maxQuantity}
								disabled={inputDisabled}
							/>
							<button
								className={`w-10 ${incrementDisabled && 'opacity-50'}`}
								onClick={() => handleAdjustQuantityPlus(line.quantity)}
								aria-label={window.TMW.Strings.cart.add}
								disabled={incrementDisabled}
							>
								&#43;
							</button>
						</div>
					)}
				</div>
				<button
					type="button"
					className="body-2 md:body-1 underline"
					onClick={() => handleRemove()}
				>
					{window.TMW.Strings.cart.remove}
				</button>
			</div>
		</div>
	);	
};

export default withCartContext(CartItem);
